import { useCallback, useEffect, useRef, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";

import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import { useAuthContext } from "@/components/AuthProvider";
import { HOME_URL, LOGIN_URL } from "@/core/urls";
import { theme } from "@/core/theme";
import { logOutViaFetch, verifyOtpSession } from "@/hooks/useOtpApi";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Center from "@/tpo/Center";
import ChevronComponent from "@/tpo/Chevron";
import Input from "@/tpo/Input";
import Stack from "@/tpo/Stack";
import AuthFlowHeader from "@/components/otp/AuthFlowHeader";
import ButtonV2 from "@/v2/Buttons";

const TOKEN_LENGTH = 6;

export default function OtpVerify({ showHeader = true }) {
  const history = useHistory();
  const location = useLocation();
  const { csrfToken, refreshAuth } = useAuthContext();
  const inputRef = useRef(null);
  const [token, setToken] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleVerify = useCallback(async () => {
    if (token.length !== TOKEN_LENGTH) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await verifyOtpSession(csrfToken, { token, remember });
      await refreshAuth();
      history.push(new URLSearchParams(location.search).get("next") || HOME_URL);
    } catch (verifyError) {
      setError(verifyError.message);
      setToken("");
    } finally {
      setSubmitting(false);
    }
  }, [csrfToken, history, location.search, refreshAuth, remember, token]);

  const handleSignOut = async () => {
    try {
      await logOutViaFetch(csrfToken);
    } catch {
      // Session may already be invalid; still redirect to login.
    }
    window.location.href = LOGIN_URL;
  };

  const content = (
    <PanelBoxV2
      maxWidth={600}
      stacked
      gap={40}
      outer={{
        pt: theme.spacing.section.pt,
        pb: theme.spacing.section.pb,
        px: 20
      }}
    >
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight="130%">
          Two-factor verification
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%" mt={20}>
          Enter the 6-digit code from your authenticator app to continue.
        </Box>
      </Box>

      <Stack gap={20}>
        <Input
          ref={inputRef}
          value={token}
          onChange={event =>
            setToken(event.target.value.replace(/\D/g, "").slice(0, TOKEN_LENGTH))
          }
          onKeyDown={event => {
            if (event.key === "Enter" && token.length === TOKEN_LENGTH) {
              handleVerify();
            }
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={TOKEN_LENGTH}
          placeholder="000000"
          disabled={submitting}
          width="100%"
          px={2}
          py={2}
          height={50}
          borderRadius={5}
          border="1px solid"
          borderColor="carbon"
          bg="white"
          fontFamily="monospace"
          fontSize={18}
          letterSpacing={4}
          textAlign="center"
        />

        <Center gap={8}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={remember}
              onChange={event => setRemember(event.target.checked)}
            />
            <Box as="span" fontFamily="gilroyMedium" fontSize={14}>
              Remember this device
            </Box>
          </label>
        </Center>

        {error ? (
          <Box color="red" fontSize={14}>
            {error}
          </Box>
        ) : null}
      </Stack>

      <Center gap={20} stacked>
        <ButtonV2
          color="green"
          type="button"
          rightIcon={<ChevronComponent />}
          size={["sm", "sm", "md"]}
          disabled={token.length !== TOKEN_LENGTH || submitting}
          onClick={handleVerify}
        >
          {submitting ? "Verifying…" : "Verify & continue"}
        </ButtonV2>
        <ButtonV2 color="dark" type="button" variant="link" onClick={handleSignOut}>
          Sign out
        </ButtonV2>
      </Center>
    </PanelBoxV2>
  );

  if (!showHeader) {
    return content;
  }

  return (
    <>
      <AuthFlowHeader />
      <CustomerPage
        jumbotronProps={{
          title: "Two-factor verification",
          bg: "dark",
          color: "white"
        }}
      >
        {content}
      </CustomerPage>
    </>
  );
}
